import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  animate?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width,
  height,
  rounded = 'md',
  animate = true
}) => {
  const roundedClasses = {
    none: '',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full'
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={`bg-gray-300 ${roundedClasses[rounded]} ${
        animate ? 'animate-pulse' : ''
      } ${className}`}
      style={style}
    />
  );
};

// Skeleton Line - for text content
interface SkeletonLineProps {
  width?: string;
  className?: string;
}

export const SkeletonLine: React.FC<SkeletonLineProps> = ({
  width = '100%',
  className = ''
}) => (
  <Skeleton
    className={`h-4 ${className}`}
    width={width}
    rounded="sm"
  />
);

// Skeleton Circle - for avatars/icons
interface SkeletonCircleProps {
  size?: number;
  className?: string;
}

export const SkeletonCircle: React.FC<SkeletonCircleProps> = ({
  size = 40,
  className = ''
}) => (
  <Skeleton
    className={className}
    width={size}
    height={size}
    rounded="full"
  />
);

// Skeleton Button
interface SkeletonButtonProps {
  width?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const SkeletonButton: React.FC<SkeletonButtonProps> = ({
  width = '120px',
  size = 'md',
  className = ''
}) => {
  const heights = {
    sm: 'h-8',
    md: 'h-10',
    lg: 'h-12'
  };

  return (
    <Skeleton
      className={`${heights[size]} ${className}`}
      width={width}
      rounded="md"
    />
  );
};

// Skeleton Card - for card containers
interface SkeletonCardProps {
  children: React.ReactNode;
  className?: string;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  children,
  className = ''
}) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
    {children}
  </div>
);

export default Skeleton;